import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Divider, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { ClientResponse, IQueryPerams } from "../../config/types";
import CustomizedSwitch from "../../components/Switch";
import { formatSnakeCaseToReadable } from "../../utils/common";
import { DatePicker } from "../../components/calender";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import { COURIER_TRANSPORT_MODES } from "../../config/constants/common";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T) => void;
  isDeleted?: boolean;
  isDistributed?: boolean;
}

interface Ifilter {
  distribution_batch_number: string;
  name_of_courier: string;
  vehicle_number: string;
  dispatch_date: string;
  is_deleted?: boolean;
}
export const DispatchLogsFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet,
  isDeleted,
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<Ifilter>({
      distribution_batch_number: "",
      name_of_courier: "",
      vehicle_number: "",
      dispatch_date: "",
      is_deleted: isDeleted,
    });

  const resetFilter = <K extends keyof Ifilter>(key: K) => {
    updateForm((p) => {
      const value = typeof p[key]?.value === "boolean" ? false : "";
      const state = {
        ...p,
        [key]: { value },
      };
      updateQuerySet((prev) => ({ ...prev, [key]: value }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    const new_data = {
      ...data,
    };
    updateQuerySet(data);
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(new_data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.distribution_batch_number.value,
    form.name_of_courier.value,
    form.vehicle_number.value,
    form.dispatch_date.value,
    form.is_deleted?.value,
  ]);

  const handleDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      dispatch_date: { value: date, isValid: true },
    }));
  };
  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      width={"100%"}
      mb={1}
    >
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading}>
          <Stack direction={"column"} spacing={2}>
            <SideBarSearchInput
              label="Distribution Batch Number"
              placeholder="Search by batch number ..."
              value={form.distribution_batch_number.value}
              handleChange={updateInputField("distribution_batch_number")}
            />
            <SideBarSearchInput
              label="Name of courier"
              placeholder="Search by name of courier .."
              value={form.name_of_courier.value}
              handleChange={updateInputField("name_of_courier")}
            />
            <SideBarSearchInput
              label="Vehicle Number"
              placeholder="Search by vehicle number .."
              value={form.vehicle_number.value}
              handleChange={updateInputField("vehicle_number")}
            />
            <DatePicker
              handleOnchange={handleDatePicker}
              label="Dispatch date"
            />

            <Divider orientation="horizontal" />

            {isDeleted !== false && (
              <CustomizedSwitch
                title="Deleted"
                onChange={updateInputField("is_deleted", "BOOLEAN")}
                value={form.is_deleted?.value ?? false}
              />
            )}
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFormValues())
            .filter(
              ([_, value]) =>
                (typeof value === "string" && value.trim() !== "") ||
                typeof value === "boolean"
            )
            .map(([key, value]) => (
              <FilterItemButton
                key={key}
                label={`By ${formatSnakeCaseToReadable(key)}`}
                tip={value.toString()}
                height={40}
                onClick={() => resetFilter(key as keyof Ifilter)}
              />
            ))}
        </Box>
      </Stack>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570,
    },
  }),
};
